import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'

import {
  AcquisitionSource,
  MembershipScopes,
} from '@/reducers/auth/userReducer'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  UPDATE_USER_KEY,
  fetchCurrentUserDetailsIfNeeded,
  updateUser,
} from '@/actions/userActions'
import {
  getCurrentUser,
  selectMembershipIsIn,
} from '@/selectors/user.selectors'

import { useSignupPageUpdate } from '@/services/onboardingService'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector, useTimeoutRef } from '@/utils/sharedHooks'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { getFieldName, makeReqStringSchema } from '@/base'
import { SIGNUP_PATHS } from '../helpers'
import { useHonoredFreeTrialLength } from '../useFreeTrialEnabled'

import { TypographyStyles } from '@/styles'
import { Alert, Button, Form, RadioGroupItem, Image, Text } from '@/ui'
import { SignupPageLayout } from '../SignupPageLayout'

const STRIPE_WEBHOOK_TIMEOUT = 3000

export const Success = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const pageView = useAnalyticsView()
  const track = useAnalyticsTrack()
  const timeoutRef = useTimeoutRef()
  const currentUser = useReselector(getCurrentUser)
  const [loading, setLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const isUpdatingUser = useReselector(getIsFetching, UPDATE_USER_KEY)
  const errorUpdatingUser = useReselector(getFetchError, UPDATE_USER_KEY)
  const trialParameterValue = searchParams.get('trial')

  const hasActiveOrTrialMembership = useReselector(
    selectMembershipIsIn,
    MembershipScopes.serviceEntitledIncludingTrial
  )

  // The param survives the redirect whether or not a trial was granted, so the kill
  // switch has to be read here too — checkout charges in full when it is off.
  const freeTrialDays = useHonoredFreeTrialLength(trialParameterValue)

  const titleText = freeTrialDays
    ? `Thank you for starting your ${freeTrialDays}-day free trial!`
    : 'Thank you for your payment!'

  useSignupPageUpdate(SIGNUP_PATHS.success)

  // Once a successful payment comes through a user will be updated to active.
  // We do not want them to navigate to their dashboard until this happens though so refetch the user with a timeout on this page
  // Note this update user state logic will not work on locally unless stripe webhooks are set up
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      dispatch(fetchCurrentUserDetailsIfNeeded(true))
      setLoading(false)
    }, STRIPE_WEBHOOK_TIMEOUT)
  }, [dispatch, timeoutRef])

  useEffect(() => {
    pageView('sign up payment success')
  }, [pageView])

  const formik = useFormik({
    initialValues: {
      acquisitionSource: null,
      acquisitionSourceComment: null,
    },
    onSubmit: async ({ acquisitionSource, acquisitionSourceComment }) => {
      // Acquisition comment should only have a value when other is selected
      // (prevent a lingering value if the user inputs something, then selects a diff option)
      let commentToPersist = acquisitionSourceComment

      if (acquisitionSource !== AcquisitionSource.other) {
        commentToPersist = null
      }

      const user = await updateUser(currentUser?.id, {
        acquisitionSource,
        acquisitionSourceComment: commentToPersist,
      })(dispatch)

      if (user) {
        navigate('/')
      }

      track('completed payment confirmation page', {
        acquisitionSource,
        acquisitionSourceComment,
      })
    },
  })

  useEffect(() => {
    // Clear acquisition comment if "other" is not selected
    if (
      formik.values.acquisitionSource !== AcquisitionSource.other &&
      formik.values.acquisitionSourceComment
    ) {
      formik.setFieldValue(
        getFieldName<typeof formik.values>('acquisitionSourceComment'),
        null
      )
    }
  }, [formik])

  const otherFieldSelected =
    formik.values.acquisitionSource === AcquisitionSource.other

  const referralSources = [
    {
      value: AcquisitionSource.webSearch,
      label: 'Web Search (Google, Bing, Yahoo, etc.)',
    },
    {
      value: AcquisitionSource.onlineAds,
      label: 'Online Ads',
    },
    {
      value: AcquisitionSource.colleagueOrFriend,
      label: 'Colleague or Friend',
    },
    {
      value: AcquisitionSource.socialMedia,
      label: 'Social Media (Facebook, Instagram, LinkedIn)',
    },
    {
      value: AcquisitionSource.events,
      label: 'Events (Webinars, Podcasts, Workshops)',
    },
    {
      value: AcquisitionSource.onlineCommunities,
      label:
        'Online Communities (Private Practice Pro, Private Practice Skills, etc.)',
    },
    {
      value: AcquisitionSource.therapyPlatforms,
      label: 'Therapy Platforms (Alma, Grow Therapy, Path, etc.)',
    },
    {
      value: AcquisitionSource.mailer,
      label: 'Mailer',
    },
    {
      value: AcquisitionSource.other,
      label: 'Other',
    },
  ]

  return (
    <SignupPageLayout>
      {errorUpdatingUser && (
        <Alert variant="error" className="my-8 text-center">
          An error occurred, please try again.
        </Alert>
      )}

      <div className="flex flex-col space-y-4 items-center">
        <Image
          src="https://heard-images.s3.us-west-1.amazonaws.com/assets/celebration_confentti.png"
          alt="celebration confetti"
          height={165}
          width={165}
        />

        <Text variant={TypographyStyles.H1} textAlign="center">
          {titleText}
        </Text>

        <FormikProvider value={formik}>
          <Form.RadioGroup
            name={getFieldName<typeof formik.values>('acquisitionSource')}
            label="How did you hear about Heard?"
          >
            <div className="flex flex-col gap-2">
              {referralSources.map((option) => (
                <RadioGroupItem
                  key={option.value}
                  value={option.value}
                  label={option.label}
                />
              ))}
            </div>
          </Form.RadioGroup>

          {otherFieldSelected && (
            <Form.Input
              name={getFieldName<typeof formik.values>(
                'acquisitionSourceComment'
              )}
              schema={makeReqStringSchema()}
              placeholder="Tell us more"
            />
          )}
        </FormikProvider>

        <Button
          id="btn-signup-payment-success"
          onClick={formik.submitForm}
          disabled={
            loading ||
            !currentUser ||
            !hasActiveOrTrialMembership ||
            isUpdatingUser
          }
          loading={loading || isUpdatingUser}
        >
          Go to Heard
        </Button>
      </div>
    </SignupPageLayout>
  )
}
